import { Card, EmptyState, Tabs, Popover, ActionList, Button, Icon } from '@shopify/polaris'
import { useCallback, useEffect, useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts'
import { getStats } from './dataset'

const options = [
    { label: '6 hours', value: '6h' },
    { label: '12 hours', value: '12h' },
    { label: '1 day', value: '1d' },
    { label: '3 days', value: '3d' },
    { label: '7 days', value: '7d' },
    { label: '14 days', value: '14d' },
    { label: '1 month', value: '1m' },
    { label: '2 months', value: '2m' },
    { label: '6 months', value: '6m' },
    { label: '1 year', value: '1y' },
]

const tabs = [
    {
        id: 'unique_views',
        content: 'Unique user views',
        accessibilityLabel: 'Unique user views',
        panelID: 'unique_views',
    },
    {
        id: 'views',
        content: 'Product page views',
        accessibilityLabel: 'Product page views',
        panelID: 'views',
    },
    {
        id: 'add_to_cart',
        content: 'Add to carts',
        accessibilityLabel: 'Add to carts',
        panelID: 'add_to_cart',
    },
    {
        id: 'units_sold',
        content: 'Units sold',
        accessibilityLabel: 'Units sold',
        panelID: 'units_sold',
    },
    {
        id: 'revenues',
        content: 'Total revenue',
        accessibilityLabel: 'Total revenue',
        panelID: 'revenues',
    },
    {
        id: 'revenue_per_view',
        content: 'Revenue per view',
        accessibilityLabel: 'Revenue per view',
        panelID: 'revenue_per_view',
    },
]

const Statistics = ({ testAB }) => {

    const [selected, setSelected] = useState('6h')
    const [selectedTab, setSelectedTab] = useState(0)
    const [stats, setStats] = useState([])
    const [popoverActive, setPopoverActive] = useState(false)

    useEffect(() => {
        const res = getStats(testAB, selected, tabs[selectedTab].id)
        setStats(res)
    }, [testAB, selected, selectedTab])

    const handleTabChange = useCallback((selectedTabIndex) => {
        setSelectedTab(selectedTabIndex)
    }, [])

    const handleSelectChange = useCallback((value) => {
        setSelected(value)
    }, [])

    const togglePopover = useCallback(() => {
        setPopoverActive((active) => !active)
    }, [])

    const formatYAxis = (value) => {
        if (tabs[selectedTab].id === 'revenues' || tabs[selectedTab].id === 'revenue_per_view') {
            return value.toFixed(2)
        }
        return Math.floor(value)
    }

    const isEmpty = () => {
        if (!stats || stats.length === 0) return true

        if (tabs[selectedTab].id === 'revenue_per_view') {
            return stats.every(item => item.revenueA === 0 && item.revenueB === 0)
        } else {
            return stats.every(item => item.variantA === 0 && item.variantB === 0)
        }
    }

    return (
        <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} fitted>
                <Card.Section>
                    <div style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <div style={{ width: 12, height: 12, background: '#458FFF', borderRadius: 2 }} />
                                    <span>Variant A</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <div style={{ width: 12, height: 12, background: '#00A47C', borderRadius: 2 }} />
                                    <span>Variant B</span>
                                </div>
                             </div>
                            <Popover
                                active={popoverActive}
                                activator={
                                    <Button
                                        plain
                                        onClick={togglePopover}
                                        accessibilityLabel="Select date range"
                                    >
                                        <img src="/statistics.png" alt="" style={{ width: 20, height: 20 }} />
                                    </Button>
                                }
                                onClose={togglePopover}
                            >
                                <ActionList
                                    items={options.map((option) => ({
                                        content: option.label,
                                        active: selected === option.value,
                                        onAction: () => {
                                            handleSelectChange(option.value)
                                            setPopoverActive(false)
                                        },
                                    }))}
                                />
                            </Popover>
                        </div>
                        {isEmpty() ? (
                            <EmptyState heading="No data available">
                                <p>There is no {tabs[selectedTab].content.toLowerCase()} data for the selected time range.</p>
                            </EmptyState>
                        ) : (
                            <div style={{ height: 300 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    {tabs[selectedTab].id === 'revenue_per_view' ?
                                        <LineChart data={stats} margin={{ top: 40, right: 20, left: 0, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                            <XAxis
                                                dataKey="views"
                                                label={{ value: 'Views', position: 'insideBottom', offset: -10 }}
                                            />
                                            <YAxis
                                                tickFormatter={formatYAxis}
                                                label={{ value: 'Revenue', angle: -90, position: 'insideLeft' }}
                                            />
                                            <Tooltip />
                                            <Line
                                                type="monotone"
                                                dataKey="revenueA"
                                                name="Variant A"
                                                stroke="#458FFF"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="revenueB"
                                                name="Variant B"
                                                stroke="#00A47C"
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                        </LineChart> :
                                        <BarChart data={stats} margin={{ top: 40, right: 20, left: 0, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                            <XAxis dataKey="label" />
                                            <YAxis
                                                tickFormatter={formatYAxis}
                                                allowDecimals={tabs[selectedTab].id === 'revenues'}
                                            />
                                            <Tooltip />
                                            <Bar dataKey="variantA" name="Variant A" fill="#458FFF" radius={[6, 6, 0, 0]} />
                                            <Bar dataKey="variantB" name="Variant B" fill="#00A47C" radius={[6, 6, 0, 0]} />
                                        </BarChart>
                                    }
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </Card.Section>
            </Tabs>
        </Card>
    )
}

export default Statistics
